import { ArrowLeft } from "lucide-react";

export function TermsOfService({ onBack }) {
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-6 transition-colors"
          data-testid="terms-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </button>

        <div className="bg-white border border-zinc-200 p-6 sm:p-10">
          <h1
            className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-1 tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Terms of Service
          </h1>
          <p className="text-sm text-zinc-400 mb-8">Last updated: August 13, 2026</p>

          <div className="space-y-6 text-sm text-zinc-700 leading-relaxed">
            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">1. Agreement to Terms</h2>
              <p>
                These Terms of Service ("Terms") govern your use of the Pallet Cut Calculator application (the "App"), operated by Michael Fisher ("we," "our," or "us"). These Terms apply to all use of the App, including any free trial period and paid subscription use. By creating an account or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.
              </p>
              <p className="mt-2">
                If you use the App on behalf of a company or other organization, you represent that you have the authority to accept these Terms for that organization, and "you" includes that organization.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">2. Description of the Service</h2>
              <p>
                The App is a business tool for pallet manufacturing operations: calculating cut lists and material requirements, tracking lumber inventory, managing customer orders, and syncing this data across your devices. The App is a calculation and management aid — it is not a substitute for professional judgment, measurement, or verification. We do not guarantee that the App will retain your data indefinitely (see Sections 5 and 11).
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">3. Eligibility & Accounts</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You must be at least 18 years old to create an account and use the App.</li>
                <li>You must provide a valid email address and keep your password confidential.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>One account may be used across multiple devices belonging to you or your crew, but you may not resell or share access outside your operation.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">4. Subscriptions, Billing & Cancellation</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>The App offers a free trial period, after which continued use requires a paid subscription (billed monthly or yearly).</li>
                <li>Payments are processed securely by <strong>Stripe</strong>, our third-party payment processor. We never see or store your full card details. Stripe processes the payment, but the subscription relationship is between you and us.</li>
                <li>Subscriptions renew automatically at the end of each billing period until canceled.</li>
                <li>You can cancel at any time from the App's Settings menu. After canceling, you keep full access until the end of your current billing period; no further charges will be made.</li>
                <li>Except where required by law, payments are non-refundable for partial billing periods. Canceling does not entitle you to a refund of the current period's payment.</li>
                <li>Prices may change. We will provide reasonable advance notice of any price change, and the new price will apply only when your next billing period begins — never in the middle of a period you have already paid for.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">5. Your Data</h2>
              <p>
                You own the data you enter into the App — pallet configurations, cut lists, inventory counts, orders, notes, and uploaded files. We store and process your data to provide the service, and also as reasonably needed for security, abuse prevention, billing and account administration, backups, and legal compliance, as described in our Privacy Policy.
              </p>
              <p className="mt-2">
                You may delete your account at any time through the App's Settings menu. Deleted accounts can be restored by logging back in within 10 days; after that, your data is permanently removed from our active systems. Deleted data may persist in routine backups for a limited additional period before those backups are cycled out.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">6. User Responsibilities</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You are responsible for entering accurate information — pallet dimensions, component specs, lumber details, quantities, and inventory counts.</li>
                <li>You are responsible for reviewing the App's calculations and output before acting on them.</li>
                <li>You are responsible for maintaining your own backups of important business information stored in the App.</li>
                <li>You make the final production, purchasing, inventory, and business decisions — not the App.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">7. Acceptable Use</h2>
              <p className="mb-2">You agree not to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use the App for any unlawful purpose.</li>
                <li>Attempt to gain unauthorized access to other users' accounts or data.</li>
                <li>Interfere with, disrupt, or overload the App's servers or networks.</li>
                <li>Reverse engineer, copy, or resell the App or any part of it.</li>
                <li>Upload files containing malware or content you do not have the right to store.</li>
                <li>Store extremely sensitive information in the App that the service does not require — such as passwords, payment card numbers, or Social Security numbers.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">8. Intellectual Property</h2>
              <p>
                We own the App itself — the software, source code, design, features, branding, logos, and all related intellectual property. Your subscription gives you a limited, non-exclusive, non-transferable right to use the App for your pallet operation while these Terms are in effect. You own your business data (Section 5); you do not acquire any ownership of the App or its software.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">9. Service Availability & Third-Party Services</h2>
              <p>
                The App is provided on an "as is" and "as available" basis. We work hard to keep it reliable, but we do not guarantee uninterrupted or error-free operation. Portions of the service depend on third-party providers — including payment processing (Stripe), email delivery, and hosting infrastructure — and the App may be affected by outages or changes in those services that are outside our control.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">10. Calculations & Business Decisions Disclaimer</h2>
              <p>
                The App's calculations — cut lists, material counts, board footage, cants, and cost estimates — are provided as a working aid. The App calculates based on the information you provide; if the inputs are wrong, the outputs will be wrong. You are responsible for checking your inputs and verifying results before making purchasing, production, or business decisions. The App is not a substitute for professional judgment or physical measurement. We are not liable for material waste, production errors, or losses resulting from reliance on calculated output.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">11. Data Loss & Backups</h2>
              <p>
                We take reasonable measures to protect and back up the data stored in the App, but we cannot guarantee that data will never be lost, corrupted, or become unavailable. You are responsible for keeping your own records and backups of business-critical information — orders, inventory, pallet configurations, and uploaded files. Do not treat the App as your only copy of information your business cannot afford to lose.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">12. Suspension & Termination</h2>
              <p>
                We may suspend or freeze accounts that violate these Terms, abuse the service, or present a security risk. Frozen accounts lose access to the App until the issue is resolved. If we terminate your account for a violation of these Terms, any remaining paid subscription time is forfeited and no refund is owed, except where required by law.
              </p>
              <p className="mt-2">
                You may stop using the App and delete your account at any time through the Settings menu. We may also discontinue the App entirely; if we do, we will provide reasonable advance notice so you can retrieve your information.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">13. Disclaimer of Warranties</h2>
              <p>
                To the maximum extent permitted by law, the App is provided without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the App will meet your requirements or that its output will be accurate, complete, or error-free.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">14. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, our total liability for any claim arising out of or relating to the App is limited to the amount you paid us in the twelve (12) months before the claim arose. We are not liable for indirect, incidental, special, or consequential damages, including lost profits or lost data. Nothing in these Terms limits or excludes any liability that cannot legally be limited or excluded under applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">15. Governing Law & Disputes</h2>
              <p>
                These Terms are governed by the laws of the Commonwealth of Pennsylvania, USA, without regard to its conflict-of-law rules. Before filing any claim, you agree to first contact us (Section 17) and give us a reasonable opportunity to resolve the dispute informally. Any dispute that cannot be resolved informally will be brought in a court of competent jurisdiction in Pennsylvania, except where applicable law requires otherwise.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">16. Changes, Severability & Entire Agreement</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Changes:</strong> We may update these Terms from time to time. Material changes will be communicated through the App or by email when reasonably appropriate, and will be reflected by updating the "Last updated" date above. Continued use of the App after changes take effect constitutes acceptance of the updated Terms.</li>
                <li><strong>Severability:</strong> If any part of these Terms is found unenforceable, that part will be limited to the minimum extent necessary and the rest of the Terms remain in full effect.</li>
                <li><strong>Entire agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and us regarding your use of the App.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-base font-bold text-zinc-900 mb-2">17. Contact</h2>
              <p>Questions about these Terms? Contact:</p>
              <p className="mt-2">
                <strong>Michael Fisher</strong><br />
                <a href="mailto:Michaelervinfisher2002@gmail.com" className="text-orange-600 hover:underline">
                  Michaelervinfisher2002@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
